import React, { useState } from 'react'
import { Check, Lock } from 'lucide-react'
import { Button, Modal, Notice, Spinner } from '../../cases/ui'
import { pluralise } from '../../cases/format'

// ======================================================================
// THE MIGRATION PROMPT
// ======================================================================
//
// The consequential moment in this whole screen, made by somebody who
// very probably does not think of it as one. They came here to rename a
// stage or add a step; what they are actually deciding is whether the
// process a number of live disciplinary cases are running under changes
// underneath the people running them.
//
// ======================================================================
// THE THREE RULES THE WORDING HAD TO SATISFY
// ======================================================================
//
//   1. BOTH ANSWERS ARE NORMAL, and the screen has to say so. This is
//      not a confirm-destructive-action dialog. "Only new cases" is a
//      perfectly ordinary answer — it is what an organisation tightening
//      its procedure mid-year usually wants — and presenting it as the
//      safe option and the other as the risky one would push people
//      into the wrong choice by tone alone. Neither is styled as a
//      danger button; neither is preselected.
//
//   2. THE NUMBERS COME FIRST, AND THEY ARE THE SERVER'S. Everything on
//      this screen is a count the backend computed by running the
//      actual migration plan (er_cases/case_types._plan_migration) —
//      the SAME function the real update then calls, so the preview
//      cannot disagree with what happens. Nothing here is estimated,
//      derived or rounded.
//
//   3. NO ALARM AND NO EUPHEMISM. A case that cannot move is not a
//      failure and not an error; it is a case that will finish under
//      the process it started under, which is the correct outcome and
//      arguably the point of versioning. It is described that way. But
//      it is also never hidden — the references are listed, because an
//      administrator who is not told which cases stayed behind cannot
//      check on them.
//
// ======================================================================
// WHY THE PREVIEW IS FETCHED BEFORE THE QUESTION IS ASKED
// ======================================================================
//
// Asking "apply to open cases?" before knowing the counts makes the
// question unanswerable: 0 cases and 40 cases deserve different answers
// and the person cannot tell which they are in. So the flow is
//
//     Save -> preview (writes nothing) -> the counts -> the choice ->
//     the real update
//
// and the modal opens in a loading state rather than with the question
// already on screen. The preview is a POST because it carries a body,
// not because it changes anything: no version, no repoint, no audit
// row.

export default function MigrationPrompt({
  open,
  onClose,
  typeLabel,
  nextVersionNo,
  preview,
  previewError,
  busy,
  onConfirm,
}) {
  const [choice, setChoice] = useState(null)

  // Reset the answer every time the dialog opens, so a previous edit's
  // choice is never inherited by the next one. A remembered answer on a
  // question this consequential is a defect, not a convenience.
  React.useEffect(() => {
    if (open) setChoice(null)
  }, [open])

  const wouldMove = preview?.migrated ?? 0
  const wouldStay = preview?.kept_on_old_version ?? 0
  const stranded = preview?.kept_references || []
  const closed = preview?.closed_not_migrated ?? 0
  const running = wouldMove + wouldStay

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      width="max-w-2xl"
      title="Apply this change to cases already open?"
      subtitle={
        typeLabel
          ? `Saving ${typeLabel} as version ${nextVersionNo}. New cases will use it from now on.`
          : undefined
      }
    >
      {previewError ? (
        <Notice tone="amber" title="The change could not be checked">
          {previewError.message ||
            'The organisation’s cases could not be checked against this version, so nothing has been saved. Try again.'}
        </Notice>
      ) : !preview ? (
        <Spinner label="Checking what this would do to cases already open" />
      ) : (
        <div className="space-y-4">
          <p className="text-[12.5px] leading-relaxed text-muted">
            {running === 0 ? (
              <>
                There are no open or paused cases on this case type, so this
                change affects nothing that is already running. Save it and new
                cases will use it.
              </>
            ) : (
              <>
                {pluralise(running, 'case is', 'cases are')} running on this case
                type right now. What happens to them is your decision, and both
                answers below are ordinary — a change to a procedure part-way
                through the year is often meant to apply only from that point on.
              </>
            )}
          </p>

          <div className="space-y-2.5">
            <Option
              selected={choice === false}
              onSelect={() => setChoice(false)}
              disabled={busy}
              icon={Lock}
              title="Only new cases"
              headline={
                running === 0
                  ? 'Nothing is running, so nothing changes.'
                  : `The ${pluralise(running, 'case', 'cases')} already open keep the process they were raised under.`
              }
            >
              Nothing about a case that is already running changes — not its
              stages, its deadlines, its reminders or how long the file is kept.
              Each one finishes under the version it started on.
            </Option>

            <Option
              selected={choice === true}
              onSelect={() => setChoice(true)}
              disabled={busy}
              icon={Check}
              title="New cases, and move the open cases that can"
              headline={
                wouldStay === 0
                  ? `All ${pluralise(wouldMove, 'open case moves', 'open cases move')} to version ${nextVersionNo}.`
                  : `${wouldMove} of ${running} open cases move to version ${nextVersionNo}. ${wouldStay} cannot.`
              }
            >
              {wouldStay === 0 ? (
                <>
                  Every case already running is sitting on a stage this version
                  still has, so all of them can move across cleanly.
                </>
              ) : (
                <>
                  A case can only move if the stage it is currently sitting on
                  still exists in the new version.{' '}
                  {pluralise(wouldStay, 'case is', 'cases are')} on a stage this
                  edit removes or renames, so{' '}
                  {wouldStay === 1 ? 'it stays' : 'they stay'} on the current
                  version and continue on that process until{' '}
                  {wouldStay === 1 ? 'it closes' : 'they close'}.
                </>
              )}
            </Option>
          </div>

          {stranded.length > 0 && (
            <div className="rounded-md border border-line bg-surface-2 px-4 py-3">
              <div className="eyebrow mb-1.5">
                {pluralise(stranded.length, 'case that cannot move', 'cases that cannot move')}
              </div>
              <p className="mb-2 text-[12px] leading-relaxed text-muted">
                These continue on their existing process until they close. They
                are not stuck and nothing is wrong with them — they are simply
                part-way through a procedure this version no longer describes.
              </p>
              <ul className="flex flex-wrap gap-x-3 gap-y-1">
                {stranded.map((ref) => (
                  <li key={ref} className="mono text-accent">
                    {ref}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* CLOSED CASES ARE STATED WHETHER OR NOT THERE ARE ANY. The
              rule is the reassuring part — a finished case is the record
              of a process that was actually followed, and rewriting it
              would make the file claim a procedure that did not exist
              while it ran. An administrator who is not told this rule
              will wonder about it. */}
          <p className="text-[11.5px] leading-relaxed text-faint">
            Closed cases are never moved, by either answer
            {closed > 0
              ? ` — ${pluralise(closed, 'closed case is', 'closed cases are')} on this case type`
              : ''}
            . A closed case is the record of a process that was actually
            followed, so it keeps the version it ran under permanently.
          </p>
        </div>
      )}

      <footer className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-line pt-4">
        <Button variant="ghost" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!preview || choice === null || busy}
          onClick={() => onConfirm(choice)}
        >
          {choice === null
            ? 'Choose an option'
            : choice
              ? 'Save and move the open cases'
              : 'Save for new cases only'}
        </Button>
      </footer>
    </Modal>
  )
}

/** One of the two answers.
 *
 *  A radio, semantically — this is a choice between two mutually
 *  exclusive options, and it is built out of a real <input type="radio">
 *  so a keyboard and a screen reader treat it as one. The visual card is
 *  the label. Neither option is preselected and neither is styled as
 *  destructive; see rule 1 at the top of this file. */
function Option({ selected, onSelect, disabled, icon: Icon, title, headline, children }) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition ${
        selected
          ? 'border-accent-line bg-accent-dim'
          : 'border-line bg-surface-2 hover:border-line-strong'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <input
        type="radio"
        name="apply-to-open-cases"
        className="sr-only"
        checked={selected}
        disabled={disabled}
        onChange={onSelect}
      />
      <span
        className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border ${
          selected ? 'border-accent-line text-accent' : 'border-line text-faint'
        }`}
        aria-hidden="true"
      >
        <Icon size={13} strokeWidth={1.9} />
      </span>
      <span className="min-w-0">
        <span className="block font-head text-[13px] font-semibold text-ink">
          {title}
        </span>
        <span className="mt-0.5 block text-[12.5px] font-medium text-muted">
          {headline}
        </span>
        <span className="mt-1 block text-[12px] leading-relaxed text-faint">
          {children}
        </span>
      </span>
    </label>
  )
}
